import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import StatisticsEffectBg from '../../components/statistic-effect-bg';
import { useLanguage } from '../../helpers/language-context';
import { supabase } from '../../supabaseClient';

const Newsfeed = () => {
    const { translations, language } = useLanguage();
    const [newsData, setNewsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isInView, setIsInView] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const fetchNews = async () => {
            const { data, error } = await supabase
                .from('newsfeed')
                .select('*')
                .order('date', { ascending: false });

            if (error) {
                console.error('Error fetching newsfeed:', error);
            } else {
                setNewsData(data);
            }
            setLoading(false);
        };

        fetchNews();
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
                    setIsInView(true);
                    observer.unobserve(entry.target);
                }
            },
            {
                threshold: 0.3,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const currentSectionRef = sectionRef.current;
        if (currentSectionRef) {
            observer.observe(currentSectionRef);
        }

        return () => {
            if (currentSectionRef) {
                observer.unobserve(currentSectionRef);
            }
        };
    }, [loading]); // Re-observe when loading is done as content appears

    const getLocalized = (item, field) => {
        return language === 'ms'
            ? (item[`${field}_ms`] || item[`${field}_en`])
            : (item[`${field}_en`] || item[`${field}_ms`]);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString(language === 'ms' ? 'ms-MY' : 'en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    if (loading) {
        return <div className="text-white">Loading news...</div>;
    }

    return (
        <StatisticsEffectBg waveSize="small">
            <div ref={sectionRef} className="w-full">
                <div className={`w-full h-full flex items-center justify-start transition-all duration-1000 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}>
                    <h1 className="text-3xl md:text-5xl lg:text-[80px] text-white mb-10 md:mb-20">
                        {translations.newsfeed.latestNews}
                    </h1>
                </div>
                <div className="w-full">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12">
                        {(() => {
                            const sortedNews = newsData.slice(0, 3);
                            return sortedNews.map((item, index) => (
                                <div
                                    key={index}
                                    style={{ transitionDelay: isInView ? `${index * 200}ms` : '0ms' }}
                                    className={`overflow-hidden flex flex-col h-full group transition-all duration-700 ease-out ${isInView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
                                        } hover:scale-105 transition-transform duration-300`}
                                >
                                    <img
                                        src={item.cover_image || item.image}
                                        alt={getLocalized(item, 'title')}
                                        className="w-full h-48 md:h-72 object-cover rounded-lg mb-4"
                                    />
                                    <div className="flex flex-col flex-grow">
                                        <div className="flex justify-start items-center gap-2 mb-2">
                                            <p className="text-sm text-[#ababab]">{formatDate(item.date)}</p>
                                        </div>
                                        <h3 className="text-xl md:text-2xl text-white mb-4 line-clamp-2">
                                            {getLocalized(item, 'title')}
                                        </h3>
                                        <p className="text-[#ababab] mb-4 flex-grow line-clamp-3">
                                            {getLocalized(item, 'subtitle')}
                                        </p>

                                        <Link
                                            to={`/media/news-release/details/${item.id}`}
                                            className="inline-block text-[#FDA729] hover:text-[#FDA729] font-medium"
                                        >
                                            {language === 'ms' ? 'Baca selanjutnya →' : 'Read more →'}
                                        </Link>
                                    </div>
                                </div>
                            ));
                        })()}
                    </div>
                </div>
            </div>
        </StatisticsEffectBg>
    );
};

export default Newsfeed;
